// CSV Template Schemas matching Supabase database structure

export const CSV_SCHEMAS = {
  rfqs: {
    name: 'RFQs',
    headers: [
      'ref_code',
      'title',
      'client_id',
      'status',
      'clearance_required',
      'work_arrangement',
      'locations',
      'internal_close_datetime',
      'client_close_datetime',
      'estimated_start_date',
      'initial_term_months',
      'extension_options',
      'description',
      'selection_criteria',
      'about_company',
      'about_team',
      'about_project',
      'criteria_response_type',
      'display_status',
    ],
    requiredFields: ['ref_code', 'title', 'client_id'],
    exampleRow: [
      'RFQ-2024-001',
      'Senior Software Developer',
      'uuid-from-clients-table',
      'inclusion_requested',
      'NV1',
      'Hybrid',
      'Canberra, ACT; Sydney, NSW',
      '2024-12-31T17:00:00+11:00',
      '2025-01-05T17:00:00+11:00',
      '2025-02-01',
      '12',
      '1 x 12 months',
      'We are seeking an experienced software developer...',
      'Demonstrated experience in software development...',
      'Leading organization in the public sector...',
      'A collaborative and innovative team...',
      'Digital transformation initiative...',
      'Addressing each criterion in 200-400 words',
      'hidden',
    ],
    notes: [
      '• ref_code, title, and client_id are required fields',
      '• ref_code must be unique',
      '• client_id must be a valid UUID from your clients table',
      '• status: inclusion_requested, invited, in_progress, submitted, pending_outcome, successful, unsuccessful',
      '• clearance_required: None, Baseline, NV1, NV2, TSPV (or leave empty)',
      '• locations: Semicolon-separated for multiple locations',
      '• Datetime format: YYYY-MM-DDTHH:MM:SS+11:00 (with timezone)',
      '• Date format: YYYY-MM-DD',
      '• initial_term_months should be a number',
      '• display_status: hidden, visible (controls public listing) - NOTE: You need to add this column to rfqs table first',
    ],
  },

  clients: {
    name: 'Clients',
    headers: [
      'name',
      'abbreviation',
      'abn',
      'primary_procurement_email',
      'is_rcti_client',
      'status',
      'notes',
    ],
    requiredFields: ['name'],
    exampleRow: [
      'Australian Federal Police',
      'AFP',
      '12345678901',
      'procurement@afp.gov.au',
      'true',
      'Active',
      'Primary law enforcement agency',
    ],
    notes: [
      '• name is a required field',
      '• abbreviation: Short acronym (e.g., AFP)',
      '• abn should be an 11-digit Australian Business Number (optional)',
      '• primary_procurement_email: Main procurement contact email',
      '• is_rcti_client: true or false',
      '• status: Active, Pending, or Inactive (defaults to Active)',
      '• notes: Any additional information about the client',
    ],
  },

  candidates: {
    name: 'Candidates',
    headers: [
      // Required fields
      'first_name',
      'last_name',
      // Personal details
      'known_as',
      'email',
      'phone',
      // Location
      'location_city',
      'location_state',
      'location_country',
      // Clearance & citizenship
      'clearance_level',
      'citizenship_status',
      // Employment details
      'payroll_type',
      'pty_ltd_abn',
      'notice_period',
      // Availability
      'availability_status',
      'availability_open_date',
      'open_to_new_roles',
      'open_to_client_contact',
      'preferred_contact_channel',
      // Professional
      'linkedin_url',
      'skills',
      // Admin
      'status',
      'notes',
    ],
    requiredFields: ['first_name', 'last_name'],
    optionalFields: [
      'known_as',
      'email',
      'phone',
      'location_city',
      'location_state',
      'location_country',
      'clearance_level',
      'citizenship_status',
      'payroll_type',
      'pty_ltd_abn',
      'notice_period',
      'availability_status',
      'availability_open_date',
      'open_to_new_roles',
      'open_to_client_contact',
      'preferred_contact_channel',
      'linkedin_url',
      'skills',
      'status',
      'notes',
    ],
    exampleRow: [
      // Required
      'John',
      'Smith',
      // Personal
      'Johnny',
      'john.smith@example.com',
      '+61 400 123 456',
      // Location
      'Canberra',
      'ACT',
      'Australia',
      // Clearance & citizenship
      'NV1',
      'Australian Citizen',
      // Employment
      'PAYG',
      '',
      '4 weeks',
      // Availability
      'open',
      '',
      'true',
      'true',
      'email',
      // Professional
      'https://www.linkedin.com/in/johnsmith',
      'JavaScript, React, Node.js, PostgreSQL, AWS',
      // Admin
      'Active',
      'Experienced full-stack developer',
    ],
    notes: [
      '• REQUIRED FIELDS: first_name, last_name (marked with * in preview)',
      '• ',
      '• OPTIONAL FIELDS (all others):',
      '• email: Must be unique if provided (format: user@domain.com)',
      '• clearance_level: None, Baseline, NV1, NV2, TSPV',
      '• location_state: ACT, NSW, VIC, QLD, SA, WA, TAS, NT',
      '• location_country: Defaults to Australia if not specified',
      '• payroll_type: PAYG, PTY LTD, Contractor, etc.',
      '• pty_ltd_abn: ABN number if payroll_type is PTY LTD (11 digits)',
      '• notice_period: e.g., "Immediate", "2 weeks", "1 month"',
      '• availability_status: open, open_soon, closed (defaults to open)',
      '• availability_open_date: Date format YYYY-MM-DD (e.g., 2025-02-01)',
      '• open_to_new_roles: true or false (defaults to true)',
      '• open_to_client_contact: true or false (defaults to true)',
      '• preferred_contact_channel: email, linkedin, or both (defaults to email)',
      '• linkedin_url: Full LinkedIn profile URL',
      '• skills: Comma or semicolon-separated skill names (e.g., "JavaScript, React, AWS")',
      '•   - Matched skills will be linked to skills_master',
      '•   - Unmatched skills will be created as "pending" for recruiter approval',
      '• status: Active, On Hold, Blacklisted, Inactive (defaults to Active)',
      '• notes: Any additional information about the candidate',
    ],
  },

  skills: {
    name: 'Skills',
    headers: [
      'name',
      'category',
      'status',
      'map_to',
    ],
    requiredFields: ['name'],
    optionalFields: ['category', 'status', 'map_to'],
    exampleRow: [
      'ISTQB',
      'certification',
      'pending',
      '',
    ],
    notes: [
      '• REQUIRED FIELDS: name (marked with * in preview)',
      '• ',
      '• name: The skill name (must be unique)',
      '• category: REQUIRED for non-mapping skills. Optional for mapping skills (inherits from canonical skill).',
      '•   - Must be one of: language, framework, platform, process, tool, certification, sfia',
      '• status: active, pending, rejected, mapping (defaults to pending if empty)',
      '• map_to: REQUIRED for status=mapping, the NAME of the canonical skill to map to',
      '•   - Leave empty for non-mapping skills',
      '•   - For mapping skills, category is inherited from the canonical skill',
      '• ',
      '• PROCESSING ORDER:',
      '•   - Non-mapping skills (active/pending/rejected) are created first',
      '•   - Then mapping skills are created with proper mapped_to_skill_id references',
      '•   - This ensures mapping targets exist before mapping skills are created',
    ],
  },
};

/**
 * Generate a CSV template file for download
 * @param {string} entityType - 'rfqs', 'clients', or 'candidates'
 * @returns {Blob} CSV file as a Blob
 */
export const generateCSVTemplate = (entityType) => {
  const schema = CSV_SCHEMAS[entityType];
  if (!schema) {
    throw new Error(`Unknown entity type: ${entityType}`);
  }

  // Create CSV content
  let csvContent = '';

  // Add notes as comments
  csvContent += '# ' + schema.name + ' Import Template\n';
  csvContent += '# Required fields: ' + schema.requiredFields.join(', ') + '\n';
  csvContent += '# \n';
  schema.notes.forEach((note) => {
    csvContent += '# ' + note + '\n';
  });
  csvContent += '# \n';
  csvContent += '# Delete these comment lines before importing\n';
  csvContent += '# \n\n';

  // Add headers
  csvContent += schema.headers.join(',') + '\n';

  // Add example row
  csvContent +=
    schema.exampleRow.map((value) => `"${value}"`).join(',') + '\n';

  // Create Blob
  return new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
};

/**
 * Download a CSV template
 * @param {string} entityType - 'rfqs', 'clients', or 'candidates'
 */
export const downloadCSVTemplate = (entityType) => {
  const blob = generateCSVTemplate(entityType);
  const schema = CSV_SCHEMAS[entityType];

  // Create download link
  const link = document.createElement('a');
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `${entityType}_import_template_${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

/**
 * Parse CSV file content
 * @param {string} csvText - CSV file content as text
 * @returns {Array} Array of objects representing rows
 */
export const parseCSV = (csvText) => {
  // Remove comment lines
  const lines = csvText
    .split('\n')
    .filter((line) => !line.trim().startsWith('#') && line.trim() !== '');

  if (lines.length < 2) {
    throw new Error('CSV file must contain at least a header row and one data row');
  }

  // Parse headers
  const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));

  // Parse data rows
  const data = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    // Simple CSV parsing (handles quoted values)
    const values = [];
    let current = '';
    let inQuotes = false;

    for (let j = 0; j < line.length; j++) {
      const char = line[j];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());

    // Create object from headers and values
    const row = {};
    headers.forEach((header, index) => {
      let value = values[index] || '';
      // Remove surrounding quotes if present
      value = value.replace(/^"|"$/g, '');
      row[header] = value;
    });

    data.push(row);
  }

  return data;
};

/**
 * Validate CSV data against schema
 * @param {Array} data - Parsed CSV data
 * @param {string} entityType - 'rfqs', 'clients', or 'candidates'
 * @returns {Object} { valid: boolean, errors: Array }
 */
export const validateCSVData = (data, entityType) => {
  const schema = CSV_SCHEMAS[entityType];
  if (!schema) {
    return { valid: false, errors: ['Unknown entity type'] };
  }

  const errors = [];

  // Check if data is empty
  if (!data || data.length === 0) {
    errors.push('CSV file contains no data rows');
    return { valid: false, errors };
  }

  // Validate each row
  data.forEach((row, index) => {
    const rowNum = index + 2; // +2 because of header row and 0-based index

    // Check required fields
    schema.requiredFields.forEach((field) => {
      if (!row[field] || row[field].trim() === '') {
        errors.push(`Row ${rowNum}: Missing required field '${field}'`);
      }
    });

    // Entity-specific validations
    if (entityType === 'rfqs') {
      // Validate dates
      if (row.internal_close_datetime && !isValidDateTime(row.internal_close_datetime)) {
        errors.push(`Row ${rowNum}: Invalid internal_close_datetime format. Use YYYY-MM-DDTHH:MM:SS`);
      }
      if (row.client_close_datetime && !isValidDateTime(row.client_close_datetime)) {
        errors.push(`Row ${rowNum}: Invalid client_close_datetime format. Use YYYY-MM-DDTHH:MM:SS`);
      }
      if (row.estimated_start_date && !isValidDate(row.estimated_start_date)) {
        errors.push(`Row ${rowNum}: Invalid estimated_start_date format. Use YYYY-MM-DD`);
      }
    } else if (entityType === 'clients') {
      // Validate ABN if provided
      if (row.abn && !/^\d{11}$/.test(row.abn.replace(/\s/g, ''))) {
        errors.push(`Row ${rowNum}: ABN must be 11 digits`);
      }
    } else if (entityType === 'candidates') {
      // Validate email
      if (row.email && !isValidEmail(row.email)) {
        errors.push(`Row ${rowNum}: Invalid email format`);
      }
      // Validate dates
      if (row.availability_open_date && !isValidDate(row.availability_open_date)) {
        errors.push(`Row ${rowNum}: Invalid availability_open_date format. Use YYYY-MM-DD`);
      }
      // Validate enums
      if (row.clearance_level && !['None', 'Baseline', 'NV1', 'NV2', 'TSPV'].includes(row.clearance_level)) {
        errors.push(`Row ${rowNum}: Invalid clearance_level. Must be: None, Baseline, NV1, NV2, or TSPV`);
      }
      if (row.availability_status && !['open', 'open_soon', 'closed'].includes(row.availability_status)) {
        errors.push(`Row ${rowNum}: Invalid availability_status. Must be: open, open_soon, or closed`);
      }
      if (row.preferred_contact_channel && !['email', 'linkedin', 'both'].includes(row.preferred_contact_channel)) {
        errors.push(`Row ${rowNum}: Invalid preferred_contact_channel. Must be: email, linkedin, or both`);
      }
      if (row.status && !['Active', 'On Hold', 'Blacklisted', 'Inactive'].includes(row.status)) {
        errors.push(`Row ${rowNum}: Invalid status. Must be: Active, On Hold, Blacklisted, or Inactive`);
      }
    } else if (entityType === 'skills') {
      // Validate category (required for non-mapping skills, optional for mapping skills)
      const validCategories = ['language', 'framework', 'platform', 'process', 'tool', 'certification', 'sfia'];
      const status = row.status?.toLowerCase() || 'pending';

      // Category is required for non-mapping skills
      if (status !== 'mapping' && !row.category) {
        errors.push(`Row ${rowNum}: Category is required for non-mapping skills`);
      }

      // If category is provided, validate it
      if (row.category && !validCategories.includes(row.category)) {
        errors.push(`Row ${rowNum}: Invalid category. Must be: ${validCategories.join(', ')}`);
      }

      // Validate status enum
      const validStatuses = ['active', 'pending', 'rejected', 'mapping'];
      if (row.status && !validStatuses.includes(row.status)) {
        errors.push(`Row ${rowNum}: Invalid status. Must be: ${validStatuses.join(', ')}`);
      }

      // If status is 'mapping', map_to must be provided
      if (row.status === 'mapping' && (!row.map_to || row.map_to.trim() === '')) {
        errors.push(`Row ${rowNum}: Skills with status='mapping' must have a 'map_to' value`);
      }

      // If status is NOT 'mapping', map_to should be empty
      if (row.status && row.status !== 'mapping' && row.map_to && row.map_to.trim() !== '') {
        errors.push(`Row ${rowNum}: Only skills with status='mapping' can have a 'map_to' value`);
      }
    }
  });

  return { valid: errors.length === 0, errors };
};

// Helper validation functions
const isValidDateTime = (dateTimeStr) => {
  const regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;
  if (!regex.test(dateTimeStr)) return false;
  const date = new Date(dateTimeStr);
  return !isNaN(date.getTime());
};

const isValidDate = (dateStr) => {
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(dateStr)) return false;
  const date = new Date(dateStr);
  return !isNaN(date.getTime());
};

const isValidEmail = (email) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};
